<script lang="ts">
	import { PUBLIC_R2_URL } from '$env/static/public';
	import Actirise, { Mode, PageType } from '$lib/components/Actirise.svelte';
	import BigSquare from '$lib/components/ArticleGrid/BigSquare.svelte';
	import SideBlock from '$lib/components/ArticleGrid/SideBlock.svelte';
	import TopSingleBlock from '$lib/components/ArticleGrid/TopSingleBlock.svelte';
	import TopWideBlock from '$lib/components/ArticleGrid/TopWideBlock.svelte';
	import LdTag from '$lib/components/LdTag.svelte';
	import SeparatorLine from '$lib/components/SeparatorLine.svelte';
	import { translations } from '$lib/i18n/translations';
	import { homepageSchema } from '$lib/utils/SchemaHelper';
	import type { PageData } from './$types';

	export let data: PageData;

	const property = data.property;
	const colors = property.colors;
	const t = translations(property.language);

	$: after = data.after;
	$: articles = data.articles;
	$: showcase = data.showcase;
	$: isSecondPage = data.isSecondPage;
	$: seo = data.seo;
</script>

<svelte:head>
	<meta property="og:image" content="https://{property.hostname}/og.jpg" />
	<meta property="og:image:type" content="image/jpeg" />
	<meta property="og:image:alt" content="{property.name} - {property.description}" />
	<meta property="og:image:width" content="1200" />
	<meta property="og:image:height" content="630" />

	<link rel="next" href="https://{property.hostname}/?after={articles[articles.length - 1]._id}" />
</svelte:head>

<Actirise mode={Mode.PAGE} type={PageType.HOME} parameters={{ custom1: data.cmp.mode }} />

<LdTag
	schema={homepageSchema(
		articles.concat(showcase?.[0]?.articles ?? [], showcase?.[1]?.articles ?? []),
		property,
		after ? t('homePageSecondPageTitle') : property.name,
		seo.description,
		after
	)}
/>

{#if isSecondPage}
	<div class="max-w-screen-lg px-4 xl:px-0 mx-auto">
		<div class="mt-5 mb-3">
			<h1 class="text-2xl font-semibold">{t('homePageSecondPageTitle')}</h1>
			<h2 class="vh">{t('homePageSecondPageHeading')}</h2>
		</div>

		<div class="grid gap-6 grid-cols-1 md:grid-cols-2">
			{#each articles as article, i}
				<TopWideBlock {article} lazy={!(i < 4)} />
			{/each}
		</div>

		{#if articles.length >= 10}
			<div class="mt-5 text-right">
				<a
					href={`?after=${articles[articles.length - 1]._id}`}
					class={`inline-flex items-center group ${colors.text} hover:${colors.hover}`}
				>
					<span class="mr-2">
						<span>{t('seemore')}</span>
						<span
							class={`block max-w-0 group-hover:max-w-full transition-all duration-500 h-0.5 ${colors.hoverBackground}`}
						></span>
					</span>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 16 16"
						fill="currentColor"
						class="w-4 h-4"
					>
						<path
							fill-rule="evenodd"
							d="M2 8a.75.75 0 0 1 .75-.75h8.69L8.22 4.03a.75.75 0 0 1 1.06-1.06l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 0 1-1.06-1.06l3.22-3.22H2.75A.75.75 0 0 1 2 8Z"
							clip-rule="evenodd"
						/>
					</svg>
				</a>
			</div>
		{/if}
	</div>
{:else}
	<div class="max-w-screen-lg px-4 xl:px-0 mx-auto">
		<h1 class="vh">{seo.title}</h1>
		<h2 class="vh">{t('latestNews')}</h2>

		<div class="flex flex-col lg:flex-row gap-6">
			<div class="basis-1/2">
				<BigSquare article={articles[0]} />
			</div>
			<div class="basis-1/2 flex flex-col justify-between space-y-4 lg:space-y-0">
				<div><SideBlock article={articles[1]} /></div>
				<div><SideBlock article={articles[2]} /></div>
				<div><SideBlock article={articles[3]} /></div>
			</div>
		</div>

		{#if property.hostname == 'www.habsolumentfan.com'}
			<div class="mt-6 rounded-xl [animation:glow_5s_infinite]">
				<a
					href="https://podcast.habsolumentfan.com/dernier"
					class="opacity-80 hover:opacity-100 transition-opacity"
					target="_blank"
				>
					<img
						class="hidden lg:block rounded-xl"
						src={PUBLIC_R2_URL + '/pool-cast-1024x250.jpg'}
						alt="Le Poolcast"
					/>
					<img
						class="lg:hidden rounded-xl mx-auto"
						src={PUBLIC_R2_URL + '/pool-cast-300x250.jpg'}
						alt="Le Poolcast"
					/>
				</a>
			</div>
		{/if}

		<div class="flex flex-col md:flex-row gap-6 mt-6">
			<div class="basis-1/2">
				<TopWideBlock article={articles[4]} lazy={true} />
			</div>
			<div class="basis-1/2">
				<TopWideBlock article={articles[5]} lazy={true} />
			</div>
		</div>

		<SeparatorLine href={`?after=${articles[5]._id}`} />

		{#if showcase.length > 0}
			<div class="flex flex-col lg:flex-row lg:gap-6 mt-3">
				{#each showcase as sc}
					<div class="basis-1/2 flex flex-col">
						<div class="grow">
							<div class="mt-5 mb-3">
								<h2 class="text-2xl font-semibold">{sc.name}</h2>
							</div>
							<div class="flex gap-6">
								{#each sc.articles as article}
									<div class="basis-1/2">
										<TopSingleBlock {article} lazy={true} />
									</div>
								{/each}
							</div>
						</div>
						<SeparatorLine href={`/${sc.slug}`} />
					</div>
				{/each}
			</div>
		{/if}
	</div>
{/if}

<style>
	@keyframes -global-glow {
		50% {
			box-shadow: 0px 0px 55px 15px rgba(0, 195, 255, 0.4);
		}
	}
</style>
